import classNames from 'classnames';
import { useRef, useState } from 'react';

import { slideUp, slideDown } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';
import Chevron from '~images/inline/chevron.inline.svg';
import ChevronTriangle from '~images/inline/triangle-right.inline.svg';

type Props = {
    className?: string;
    id?: string;
    title?: string | React.ReactElement;
    class?: string;
    theme?: string;
    expanded?: string;
    waTrackingAction?: string;
};

const AccordionItem = ({
    id,
    title,
    children,
    className,
    theme,
    expanded,
    waTrackingAction,
    ...rest
}: React.PropsWithChildren<Props>) => {
    delete rest.class;
    const [isOpen, setIsOpen] = useState(expanded && expanded === 'true' ? true : false);
    const contentRef = useRef(null as HTMLDivElement | null);
    id = id || `accordion-${Math.random().toString(36).substring(2, 9)}`;

    const toggleContent = () => {
        if (!contentRef.current) {
            return;
        }
        isOpen ? slideUp(contentRef.current) : slideDown(contentRef.current);
        setIsOpen(!isOpen);
        trackToggleEvent();
    };

    const trackToggleEvent = () => {
        if (waTrackingAction) {
            fireTrackEvent({ page: { action: waTrackingAction } });
        }
    };

    const editedId = id.toLowerCase().replace(/[^a-z0-9]/gi, '');

    return (
        <li
            className={
                isOpen
                    ? theme === 'shine'
                        ? `py-2 expanded border-2 border-primary ${className || ''}`
                        : `border-b border-gray-dark py-2 expanded ${className || ''}`
                    : theme === 'shine'
                      ? `py-2 collapsed border-2 border-transparent ${className || ''}`
                      : `border-b border-gray-dark py-2 ${className || ''}`
            }
            data-qa="accordion-row"
            {...rest}
        >
            <button
                className="w-full flex items-center justify-between text-left font-bold"
                onClick={toggleContent}
                aria-expanded={isOpen}
                id={`btn-${editedId}`}
                aria-controls={`content-${editedId}`}
                data-qa="accordion-row"
            >
                {title}
                {theme === 'shine' && (
                    <ChevronTriangle
                        aria-hidden="true"
                        data-qa="accordion-arrow"
                        width="24"
                        height="24"
                        className={classNames(
                            'mr-2',
                            'text-primary',
                            isOpen ? 'transform rotate-90' : ''
                        )}
                    />
                )}
                {theme !== 'shine' && (
                    <Chevron
                        aria-hidden="true"
                        data-qa="accordion-arrow"
                        className={isOpen ? 'transform rotate-180' : ''}
                    />
                )}
            </button>
            <div
                style={!isOpen ? { display: 'none' } : { display: 'block' }}
                ref={contentRef}
                id={`content-${editedId}`}
                aria-labelledby={`btn-${editedId}`}
                className={theme === 'shine' ? 'p-0' : 'pt-4'}
            >
                {children}
            </div>
        </li>
    );
};

export default AccordionItem;
